import { useEffect, useMemo, useRef } from 'react';
import { useOutletContext, useParams, useSearchParams } from 'react-router';
import { Skeleton, Spinner } from '@monksflow/monks-ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useApprovalChainsEnabled } from '@/shared/hooks/useApprovalChainsEnabled';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useAssetApprovalChain } from '@/entities/asset-approval-chain';
import { projectFileQuery } from '@/entities/project-file';
import { projectFolderFilesQuery, projectFolderQuery } from '@/entities/project-folder';
import { useProjectCapabilities } from '@/entities/user';
import {
  clearCommentsStore,
  initCommentsStore,
  MediaPlayerProvider,
  useSelectedVersion,
} from '@/features/comment-on-file';
import { DetailSidebarViewToggle, useDetailSidebar } from '@/shared/ui';
import { useHideAppSidebar } from '@/widgets/app-shell';
import { EmptyState } from '@/shared/ui/EmptyState';
import { assetViewerTransition, eases } from '@/shared/constants/motion.constants';
import type { ProjectDetailContext } from '../Folders/folders.types';
import { mergeVersionIntoAssetFile, toAssetFile } from './assetDetail.types';
import { getGlideDirection, shouldShowApprovalPanel } from './assetDetail.utils';
import { AssetApprovalPrimary } from './components/AssetApprovalPrimary';
import { AssetDetailHeader } from './components/AssetDetailHeader';
import { AssetFilmstrip } from './components/AssetFilmstrip';
import { AssetPreviewOverlay } from './components/AssetPreviewOverlay';
import { PreviewZoomProvider } from './components/AssetPreviewOverlay/state/preview-zoom.store';
import { AssetSidebar } from './components/AssetSidebar';
import { AssetDetailFullScreen } from './components/Fullscreen';
import { PdfViewerProvider } from './components/PdfViewer/pdfViewer.store';
import { useAssetApproval } from './hooks/useAssetApproval';
import { useAutoSelectFirstSibling } from './hooks/useAutoSelectFirstSibling';
import { useTranslation } from 'react-i18next';
import { InfoIcon, MessageSquareIcon, ListChecksIcon } from 'lucide-react';

/**
 * Horizontal glide variants for next/previous navigation. `custom`
 * carries the direction (+1 forward / -1 back / 0 none): the incoming preview
 * enters from the side it's heading toward and the outgoing one leaves the
 * opposite side, so the image "glides" while the header, filmstrip and comments
 * stay fixed. Direction 0 (initial open, reduced motion) degrades to a plain
 * opacity crossfade so the thumbnail→viewer morph can play instead of a slide.
 */
const glideVariants = {
  enter: (direction: number) => ({
    opacity: 0,
    x: direction * assetViewerTransition.glideOffset,
  }),
  center: { opacity: 1, x: 0 },
  exit: (direction: number) => ({
    opacity: 0,
    x: direction * -assetViewerTransition.glideOffset,
  }),
};

const ASSET_SIDEBAR_WIDTH_KEY = 'asset-detail-sidebar-width';

export function AssetDetail() {
  const { t } = useTranslation('projects');

  const { folderId, fileId } = useParams<{ folderId: string; fileId: string }>();
  const { project, chainDrawer } = useOutletContext<ProjectDetailContext>();

  // keepPreviousData prevents the entire layout from being replaced by a spinner
  // while a new fileId's query resolves — the previous file's data stays visible
  // until the new one arrives, so the header + filmstrip don't flicker.
  const { data: file, isFetching: isFileFetching } = useQuery({
    ...projectFileQuery(project.platformProjectId, fileId),
    placeholderData: keepPreviousData,
  });

  const { data: folder } = useQuery(projectFolderQuery(project.platformProjectId, folderId));

  const [searchParams] = useSearchParams();
  const rawStatus = searchParams.get('status');
  const filmstripStatus =
    rawStatus === 'to_review' || rawStatus === 'approved' ? rawStatus : undefined;

  const { data: siblingFilesData, isFetching: isSiblingFilesLoading } = useQuery(
    projectFolderFilesQuery(project.platformProjectId, folderId, { status: filmstripStatus }),
  );

  // The assignment control needs the approval-chain rollout AND the
  // project:edit-details capability.
  const approvalChainsEnabled = useApprovalChainsEnabled();
  const projects = useProjectCapabilities();

  const sidebar = useDetailSidebar<'approval' | 'details' | 'comments'>({
    widthStorageKey: ASSET_SIDEBAR_WIDTH_KEY,
    defaultView: 'comments',
  });

  const deepLinkedCommentId = searchParams.get('cid');
  const { openView: openSidebarView } = sidebar;
  // Keyed on the id alone, never on the open view: a reader who closes the rail
  // after following the link must not have it reopened under them.
  useEffect(() => {
    if (deepLinkedCommentId) openSidebarView('comments');
  }, [deepLinkedCommentId, openSidebarView]);

  // Keyed on `platformProjectId` — the id the workspace-api routes are scoped to (the RBAC
  // guard resolves `/auth/permissions` against it). The sibling `AssetApprovalChainControl`
  // seeds the same key. Resolves a 404 to `null`, which is how an asset with no chain reads.
  const { data: assetApprovalChain, isLoading: isLoadingApprovalChain } = useAssetApprovalChain(
    project.platformProjectId,
    fileId,
  );

  const approval = useAssetApproval({
    projectId: project.platformProjectId,
    folderId: folderId!,
    fileId: fileId!,
    fileApproved: file?.status === 'approved',
    canApproveAssets: projects.canApproveAssets,
  });

  const showApprovalPanel = shouldShowApprovalPanel({
    workflowEnabled: approvalChainsEnabled,
    hasAssignedChain: !!assetApprovalChain,
    canManageChain: projects.canAssignApprovalChains,
  });

  useEffect(() => {
    if (!fileId) return;
    initCommentsStore({
      projectId: project.platformProjectId,
      targetType: 'file',
      targetId: fileId,
      workspaceId: project.workspaceId,
      platformProjectId: project.platformProjectId,
    });
  }, [fileId, project.workspaceId, project.platformProjectId]);

  useEffect(() => () => clearCommentsStore(), []);

  useHideAppSidebar();

  const { selectedVersion } = useSelectedVersion();

  const previewFile = useMemo(() => {
    if (!file) return null;
    const normalized = toAssetFile(file);
    return selectedVersion ? mergeVersionIntoAssetFile(normalized, selectedVersion) : normalized;
  }, [file, selectedVersion]);

  const siblingFiles = useMemo(() => siblingFilesData?.items ?? [], [siblingFilesData?.items]);

  // Horizontal glide direction for next/previous navigation.
  // Derived from the *rendered* preview id delta — not the URL `fileId` — so the
  // direction and the glide itself stay in lockstep with `keepPreviousData`:
  // the swap fires when the new image actually arrives, not the instant the URL
  // changes. The filmstrip and fullscreen nav buttons stay dumb (they just
  // navigate).
  //
  // The direction is computed exactly ONCE, on the render where the preview id
  // changes, and pinned for the rest of that navigation. AnimatePresence reads
  // `custom` *live* for the exiting pane, so if we recomputed every render the
  // value would fall back to 0 the moment `prevId` caught up (which happens on
  // any incidental re-render mid-animation — the fetching flag settling, the
  // comments store init, the filmstrip scroll). That would re-resolve the
  // outgoing image's exit to `x: 0` and it would fade instead of glide — the
  // intermittent "sometimes it doesn't glide". Pinning keeps both panes in sync.
  const shouldReduceMotion = useReducedMotion();
  const previewId = previewFile?.id;
  const prevPreviewIdRef = useRef<string | undefined>(undefined);
  const glideDirectionRef = useRef<-1 | 0 | 1>(0);
  if (previewId !== prevPreviewIdRef.current) {
    glideDirectionRef.current = getGlideDirection(
      prevPreviewIdRef.current,
      previewId,
      siblingFiles,
    );
    prevPreviewIdRef.current = previewId;
  }
  const glideDirection = shouldReduceMotion ? 0 : glideDirectionRef.current;

  useAutoSelectFirstSibling({
    projectId: project.platformProjectId,
    folderId,
    fileId,
    siblingFiles,
    isSiblingFilesLoading,
  });

  const isPreviewLoading = !previewFile && isFileFetching;

  return (
    // Page layout mirrors the Brief detail page: the project shell's
    // `p-app-inset` supplies the canvas inset, and the page floats three
    // surfaces — header / main / comments — separated by the same step.
    <div className="flex min-h-0 flex-1 flex-col gap-app-inset">
      <AssetDetailHeader
        folderName={folder?.name ?? '...'}
        folderAncestors={folder?.breadcrumbs ?? []}
        fileName={file?.fileName ?? '...'}
        projectId={project.platformProjectId}
        folderId={folderId!}
        fileId={fileId!}
        fileType={file?.fileType ?? ''}
        latestVersionId={file?.latestVersionId ?? null}
        primaryAction={
          <AssetApprovalPrimary
            approval={approval}
            chain={approvalChainsEnabled ? assetApprovalChain : null}
            isLoadingChain={approvalChainsEnabled && isLoadingApprovalChain}
            rail={
              showApprovalPanel
                ? {
                    isOpen: sidebar.view === 'approval',
                    onToggle: () => sidebar.toggle('approval'),
                  }
                : undefined
            }
          />
        }
        sidebarToggle={
          <>
            <DetailSidebarViewToggle
              icon={<MessageSquareIcon className="size-4" />}
              view="comments"
              openView={sidebar.view}
              onToggle={sidebar.toggle}
              hideLabel={t('projectDetail.assetSidebar.hideCommentsPanel')}
              showLabel={t('projectDetail.assetSidebar.showCommentsPanel')}
              dataTestId="asset-sidebar-toggle-comments"
            />
            <DetailSidebarViewToggle
              icon={<InfoIcon className="size-4" />}
              view="details"
              openView={sidebar.view}
              onToggle={sidebar.toggle}
              hideLabel={t('projectDetail.assetSidebar.hideDetailsPanel')}
              showLabel={t('projectDetail.assetSidebar.showDetailsPanel')}
              dataTestId="asset-sidebar-toggle-details"
            />
            {/* Only until a chain is assigned. From then on the rail is opened from the
                primary action, which carries the chain's progress — a manager assigning
                a first chain still needs a way in. */}
            {showApprovalPanel && !assetApprovalChain && (
              <DetailSidebarViewToggle
                icon={<ListChecksIcon className="size-4" />}
                view="approval"
                openView={sidebar.view}
                onToggle={sidebar.toggle}
                showLabel={t('projectDetail.assetSidebar.showApprovalPanel')}
                hideLabel={t('projectDetail.assetSidebar.hideApprovalPanel')}
                dataTestId="asset-sidebar-toggle-approval"
              />
            )}
          </>
        }
      />

      <PreviewZoomProvider>
        <PdfViewerProvider>
          <MediaPlayerProvider>
            {/* No gap here: the sidebar's resize handle owns a track the width
                of the page inset, which recreates the main↔sidebar spacing, so a
                row gap would double it. */}
            <div className="flex min-h-0 flex-1">
              {/* Main surface: preview + filmstrip as one rounded card, matching
                  the Brief editor card. The filmstrip already supplies the white
                  bottom (`rounded-bl/br-lg`); the card completes the top. */}
              <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg bg-card">
                <AssetDetailFullScreen
                  file={previewFile}
                  siblingFiles={siblingFiles}
                  projectId={project.platformProjectId}
                  folderId={folderId!}
                >
                  <div
                    data-annotation-overlay
                    className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
                  >
                    {isPreviewLoading && (
                      <div
                        data-testid="asset-preview-skeleton"
                        aria-busy="true"
                        aria-live="polite"
                        className="flex flex-1 items-center justify-center p-4"
                      >
                        <Skeleton className="h-full w-full max-w-3xl rounded-lg" />
                      </div>
                    )}

                    {!isPreviewLoading && previewFile && (
                      <>
                        {/* Horizontal glide on next/previous. Each
                            sibling gets a fresh key, so old + new panes coexist
                            and slide simultaneously (absolute inset-0). The inner
                            img's `layout="position"` morph only fires on open —
                            fresh mounts here have no prior snapshot — so the slide
                            and the open morph never fight. `initial={false}`
                            keeps the first paint static so the open morph (and
                            page-level crossfade) own the entrance. */}
                        <AnimatePresence initial={false} custom={glideDirection}>
                          <motion.div
                            key={previewFile.id}
                            custom={glideDirection}
                            variants={glideVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{
                              duration: assetViewerTransition.glideDuration,
                              ease: eases.expoOut,
                            }}
                            className="absolute inset-0 flex min-h-0 min-w-0 flex-col"
                          >
                            <AssetPreviewOverlay file={previewFile} />
                          </motion.div>
                        </AnimatePresence>
                        {isFileFetching && (
                          <div
                            aria-live="polite"
                            className="pointer-events-none absolute right-3 top-3 z-20 flex items-center gap-1.5 rounded-full bg-card/90 px-2.5 py-1 text-xs text-muted-foreground shadow"
                          >
                            <Spinner className="size-3.5" />
                            <span>{t('projectDetail.assetDetail.updating')}</span>
                          </div>
                        )}
                      </>
                    )}

                    {!isPreviewLoading && !previewFile && (
                      <EmptyState
                        title={t('projectDetail.assetDetail.assetNotFound')}
                        height="100%"
                        description={t('projectDetail.assetDetail.thisFileCouldNotBe')}
                      />
                    )}
                  </div>
                </AssetDetailFullScreen>

                <AssetFilmstrip
                  files={siblingFiles}
                  currentFileId={fileId!}
                  projectId={project.platformProjectId}
                  folderId={folderId!}
                  isLoading={isSiblingFilesLoading}
                />
              </div>

              {previewFile && fileId && (
                <AssetSidebar
                  file={previewFile}
                  projectId={project.platformProjectId}
                  assetId={fileId}
                  folderId={folderId}
                  showApprovalPanel={showApprovalPanel}
                  canManageApprovalChain={projects.canAssignApprovalChains}
                  view={sidebar.view}
                  width={sidebar.width}
                  onResize={sidebar.setWidth}
                  onResizeEnd={sidebar.commitWidth}
                  onEditApprovalChain={(approvalChainId) =>
                    chainDrawer.openEditor({ approvalChainId })
                  }
                />
              )}
            </div>
          </MediaPlayerProvider>
        </PdfViewerProvider>
      </PreviewZoomProvider>
    </div>
  );
}
